import React, { useContext, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import LiveVideoPlayer from "./LiveVideoPlayer";
import { useDispatch, useSelector } from "react-redux";
import { DockWrapperContext } from "../../Dock/DockWrapper";
import { initComponentInstance } from "orion-components/SharedComponents/ComponentState/redux/actions";

const propTypes = {
	camera: PropTypes.shape({
		id: PropTypes.string.isRequired,
		player: PropTypes.shape({
			type: PropTypes.string.isRequired,
			url: PropTypes.string
		})
	}).isRequired,
	instanceId: PropTypes.string,
	fillAvailable: PropTypes.bool,
	entityId: PropTypes.string,
	entityType: PropTypes.string,
	currentReplayMedia: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	setCameraPriority: PropTypes.func,
	readOnly: PropTypes.bool,
	playbackStartTime: PropTypes.oneOfType([PropTypes.date, PropTypes.object]),
	playBarValue: PropTypes.oneOfType([PropTypes.date, PropTypes.object]),
	playbackPlaying: PropTypes.bool,
	addReplayMedia: PropTypes.func,
	removeReplayMedia: PropTypes.func,
	expanded: PropTypes.bool,
	isOnAlertProfile: PropTypes.bool,
	isVideoWall: PropTypes.bool,
	alertDateTime: PropTypes.any,
	clusterProps: PropTypes.object
};

const defaultProps = {
	camera: { player: { url: null } },
	instanceId: "",
	entityId: "",
	entityType: "",
	fillAvailable: false,
	isOnAlertProfile: false,
	isVideoWall: false,
	isOnProfile: false,
	alertDateTime: "",
	clusterProps: {},
	hideRemoveFromWall: false
};
let isProfileHidden = false;
let isContentWrapperHidden = false;
let isAppbarHidden = false;
let isSettingsAppNavHidden = false;
let fullScreenButtonOldVideoPlayer = false;

const hiddenElements = {
	isProfileHidden,
	isContentWrapperHidden,
	isAppbarHidden,
	isSettingsAppNavHidden,
	fullScreenButtonOldVideoPlayer
};

// Function to show elements
function showElements(elementConfigurations) {
	elementConfigurations.forEach(({ key, selector, showStyles }) => {
		if (hiddenElements[key]) {
			document.querySelectorAll(selector).forEach(el => {
				applyStyles(el, showStyles);
			});
			hiddenElements[key] = false;
		}
	});
}

// Function to hide elements
function hideElements(elementConfigurations) {
	elementConfigurations.forEach(({ key, selector, hideStyles }) => {
		if (!hiddenElements[key]) {
			document.querySelectorAll(selector).forEach(el => {
				applyStyles(el, hideStyles);
			});
			hiddenElements[key] = true;
		}
	});
}

// Helper function to apply styles
function applyStyles(element, styles) {
	Object.entries(styles).forEach(([prop, value]) => {
		if (prop.startsWith("--")) {
			element.style.setProperty(prop, value);
		} else {
			element.style[prop] = value;
		}
	});
}

const COMPONENT_KEY = "cbComponents.newVideoPlayer.videoPlayerContainer";

const VideoPlayerContainer = ({
	camera,
	instanceId,
	entityId,
	entityType,
	fillAvailable,
	readOnly,
	playbackStartTime,
	playBarValue,
	playbackPlaying,
	currentReplayMedia,
	addReplayMedia,
	removeReplayMedia,
	expanded,
	isOnAlertProfile,
	isVideoWall,
	removeFromWall,
	isOnProfile,
	alertDateTime,
	clusterProps,
	hideRemoveFromWall,
	defaultToRecorded,
	playbackStartOffset,
	initialRecordedDuration,
	initialRecordedTimeUnit
}) => {
	const dockData = useSelector(state => state.appState?.dock?.dockData);
	const appId = useSelector(state => state.application.appId);
	const cameraDock = useSelector(state => state.appState?.dock?.cameraDock);
	const WavCamOpen = useSelector(state => state.appState.dock.dockData.WavCam);
	const dockCtx = useContext(DockWrapperContext);
	const handlePanelWidth = dockCtx?.handlePanelWidth ?? (() => {});

	const [isInFullScreen, setIsInFullScreen] = useState(false);
	const checkIfVideoPlayerInsideAppbar = document.querySelector(".bccVideoPlayerContainer");
	const dispatch = useDispatch();

	useEffect(() => {
		if (!instanceId) return; // optional guard

		dispatch(initComponentInstance(COMPONENT_KEY, instanceId, {}));
		dispatch(
			initComponentInstance(COMPONENT_KEY, instanceId, {
				components: { recorded: { jumpToDifferentTime: { isOpen: false } } }
			})
		);
	}, [dispatch, instanceId]);

	const elementConfigurations = [
		{
			key: "isSettingsAppNavHidden",
			selector: ".settings-navigation-panel",
			showStyles: { display: "block" },
			hideStyles: { display: "none" }
		},
		{
			key: "isDockControlHidden",
			selector: ".dock-control.open",
			showStyles: { display: "block", animation: "slideInDock 0.001s ease-in-out 0.001s 1" },
			hideStyles: { display: "none", "--animationSwitch": "none" }
		},
		{
			key: "isProfileHidden",
			selector: ".secondary-panel.list-open",
			showStyles: {
				...(!isOnProfile && { "z-index": "99999" }),
				"--animationSwitch": "0.001s ease-in-out 0.001s 1 slideInProfile"
			},
			hideStyles: {
				...(!isOnProfile && { "z-index": "0" }),
				"--animationSwitch": "none"
			}
		},
		{
			key: "isContentWrapperHidden",
			selector: ".content-wrapper",
			showStyles: { height: `calc(100vh - ${WavCamOpen ? "288px" : "48px"})` },
			hideStyles: { height: "0px" }
		},
		{
			key: "fullScreenButtonOldVideoPlayer",
			selector: ".fullScreenButtonOldVideoPlayer",
			showStyles: { zIndex: "1300" },
			hideStyles: { zIndex: "0" }
		},
		{
			key: "isAppbarHidden",
			selector: "#CB2_appBar",
			showStyles: checkIfVideoPlayerInsideAppbar?.closest("#CB2_appBar")
				? {} // don’t apply anything
				: { opacity: "1" },
			hideStyles: checkIfVideoPlayerInsideAppbar?.closest("#CB2_appBar")
				? {} //don’t apply anything
				: { opacity: "0" }
		}
	];

	useEffect(() => {
		return () => {
			if (isOnProfile) {
				const profileSideBar = document.getElementsByClassName("secondary-panel list-open");
				for (let i = 0; i < profileSideBar.length; i++) {
					profileSideBar[i].style.setProperty("--animationSwitch", " 0.5s ease-in-out 0.5s 1 slideInProfile");
				}
			}
			if (isOnAlertProfile) {
				handlePanelWidth(false);
				showElements(elementConfigurations);
			}
		};
	}, [isOnProfile, isOnAlertProfile]);

	const toggleFullScreenCameraDialog = useCallback(value => {
		setIsInFullScreen(value);
		//This block of code will reset the profile, app bar, and list panel styles to their default settings when video player is not in fullscreen mode.
		if (!value) {
			showElements(elementConfigurations);

			if (isOnAlertProfile) {
				handlePanelWidth(false);
			}
		}
	}, []);

	const iscameraInDock =
		dockData.isOpen && dockData.tab === "Cameras" && cameraDock.dockedCameras.includes(camera.id);

	useEffect(() => {
		if (isInFullScreen) {
			hideElements(elementConfigurations);

			if (isOnAlertProfile) {
				handlePanelWidth(true);
			}
		}
	}, [isInFullScreen, isOnProfile, appId]);
	return (
		<div
			style={{
				...(isInFullScreen && {
					position: "fixed",
					top: 0,
					left: 0,
					zIndex: 1300,
					background: "#000000"
				}),
				width: "100%",
				height: "100%"
			}}
		>
			<div
				className="bcc_header"
				id={`bcc_header_${instanceId}`}
				style={{
					...((!iscameraInDock || isVideoWall) && {
						width: "100%",
						height: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						backgroundColor: "#000000"
					})
				}}
			>
				<LiveVideoPlayer
					camera={camera}
					key={instanceId}
					instanceId={instanceId}
					entityId={entityId}
					entityType={entityType}
					inDock={iscameraInDock}
					fillAvailable={fillAvailable}
					readOnly={readOnly}
					playbackStartTime={playbackStartTime}
					playBarValue={playBarValue}
					playbackPlaying={playbackPlaying}
					currentReplayMedia={currentReplayMedia}
					addReplayMedia={addReplayMedia}
					removeReplayMedia={removeReplayMedia}
					expanded={expanded}
					isInFullScreen={isInFullScreen}
					isOnAlertProfile={isOnAlertProfile}
					isInVideoWall={isVideoWall}
					removeFromWall={removeFromWall}
					toggleFullScreenCameraDialog={toggleFullScreenCameraDialog}
					isOnProfile={isOnProfile}
					alertDateTime={alertDateTime}
					defaultToRecorded={defaultToRecorded}
					playbackStartOffset={playbackStartOffset}
					initialRecordedDuration={initialRecordedDuration}
					initialRecordedTimeUnit={initialRecordedTimeUnit}
					clusterProps={clusterProps}
					hideRemoveFromWall={hideRemoveFromWall}
				/>
			</div>
		</div>
	);
};

VideoPlayerContainer.propTypes = propTypes;
VideoPlayerContainer.defaultProps = defaultProps;

export default VideoPlayerContainer;
